/**
 * İzin okuyucuları (M3 §3.1.6).
 *
 * Sıra sabittir ve ilk CEVAP VEREN kazanır: sitede iki izin yöneticisi birden kuruluysa
 * (olur — tema biriyle, eklenti ötekiyle gelir) hangisinin okunacağı tesadüfe kalmaz.
 *
 * Hiçbiri yoksa mod 0'dır. "İzin bulunamadı" ile "izin verildi" arasındaki farkı varsaymıyoruz:
 * TR ve AB'de varsayılan izinsizliktir ve bu kod o varsayılanı zorlar.
 *
 * Burada ÜÇ okuyucu var: Shopify, WP Consent API ve Google Consent Mode v2 — şartnamenin D1
 * listesi. Cookiebot, OneTrust ve CookieYes D2'dir ve kendi bannerımızla birlikte gelecek;
 * IAB TCF ise hiç yok, çünkü cevabı geri çağırmayla geliyor ve amaç eşlemesi kararlaşmadı ([D]).
 * Yarım bir eşleme, izni olduğundan GENİŞ okurdu.
 *
 * Sınırın ikinci sebebi bütçe: yükleyici 5 KB gzip ve her okuyucu o bütçeden yiyor.
 */

declare global {
  interface Window {
    /**
     * Shopify'ın kendi genel nesnesi. Bildirim TEK yerde ve burada: iki parça okuyor (izin
     * okuyucusu ve sohbetin sepete ekleme modülü) ve ikinci bir bildirim, bir gün ikisinin
     * farklı şekil beklemesi demekti.
     */
    readonly Shopify?: {
      readonly customerPrivacy?: {
        analyticsProcessingAllowed?: () => boolean;
        marketingAllowed?: () => boolean;
      };
      /** Dilin kökü (`/` ya da `/en/`); vitrinin sepet uçları bununla kuruluyor. */
      readonly routes?: { readonly root?: string };
    };
    wp_has_consent?: (category: string) => boolean;
    dataLayer?: unknown[];
  }

  interface Navigator {
    readonly globalPrivacyControl?: boolean;
  }
}

export type ConsentMode = 0 | 1 | 2;

/** Bir okuyucunun cevabı: iki ayrı izin. Okuyucu yoksa `undefined` döner ve sıradakine geçilir. */
type Answer = { readonly analytics: boolean; readonly marketing: boolean } | undefined;

const shopify = (): Answer => {
  const privacy = window.Shopify?.customerPrivacy;
  if (privacy?.analyticsProcessingAllowed === undefined) return undefined;
  return {
    analytics: privacy.analyticsProcessingAllowed(),
    marketing: privacy.marketingAllowed?.() === true,
  };
};

const wordpress = (): Answer => {
  const has = window.wp_has_consent;
  if (has === undefined) return undefined;
  return { analytics: has('statistics'), marketing: has('marketing') };
};

/**
 * Google Consent Mode v2. `dataLayer`'daki `consent` komutları sırayla uygulanıyor: `default`
 * ilk hâli, `update` sonrakini yazıyor. Son söz geçerli.
 */
function consentMode(): Answer {
  const layer = window.dataLayer;
  if (!Array.isArray(layer)) return undefined;
  let analytics: string | undefined;
  let ads: string | undefined;
  let seen = false;
  for (const entry of layer) {
    if (!Array.isArray(entry) || entry[0] !== 'consent') continue;
    const state = entry[2];
    if (typeof state !== 'object' || state === null) continue;
    const record: Record<string, unknown> = { ...state };
    seen = true;
    if (typeof record['analytics_storage'] === 'string') analytics = record['analytics_storage'];
    if (typeof record['ad_storage'] === 'string') ads = record['ad_storage'];
  }
  return seen ? { analytics: analytics === 'granted', marketing: ads === 'granted' } : undefined;
}

const READERS = [shopify, wordpress, consentMode];

/**
 * Şu anki izin modu. GPC yalnız AŞAĞI indirir: tarayıcı "pazarlama istemiyorum" diyorsa
 * sitedeki izin yöneticisi ne derse desin mod 2'ye çıkılmaz.
 */
export function readConsent(): ConsentMode {
  for (const reader of READERS) {
    const answer = reader();
    if (answer === undefined) continue;
    if (!answer.analytics) return 0;
    return answer.marketing && navigator.globalPrivacyControl !== true ? 2 : 1;
  }
  return 0;
}
